export function programDataContext(config) {
  return {
    showSidebar: false,

    isProgramAccessible: false,
    isProgramStarted: false,

    numOfDays: 0,
    currentDay: 0,
    progress: 0,

    firstUndoneDay: 0,

    days: [],

    isNavigationBarSticky: null,

    contentObserver: null,

    showStartProgramHint: false,

    commentComponent: null,

    inUserList: false,
    isPaid: false,
    toggleListUri: null,
    toggleListForceUri: null,
    loading: false,

    resetProgressUri: null,

    isResetProgressModalOpen: false,

    init() {
      const programData = JSON.parse(this.$el.dataset.program);
      this.days = programData?.days ?? [];

      this.isProgramAccessible = Boolean(JSON.parse(this.$el.dataset.isProgramAccessible || 0));
      this.isProgramStarted = Boolean(JSON.parse(this.$el.dataset.isProgramStarted || 0));

      this.inUserList = this.$el.dataset.inUserList;
      this.isPaid = this.$el.dataset.isPaid;

      this.toggleListUri = this.$el.dataset.toggleListUri;
      this.toggleListForceUri = this.$el.dataset.toggleListForceUri;

      this.resetProgressUri = this.$el.dataset.resetProgressUri;

      this.numOfDays = Number(this.$el.dataset.numOfDays || 0);

      this.updateFirstUndoneDay();

      const hashM = window.location.hash.match(/^#(\/(?<target>(([0-9]+)?|current)))$/);
      if (hashM) {
        const target = hashM.groups?.target;
        if (target == "current") {
          this.$nextTick(() => this.goToFirstUndoneDay());
        } else {
          this.$nextTick(() => this.goToDay(Number(target)));
        }
      }

      this.contentObserver = new IntersectionObserver(
        (entries, o) => {
          const e = entries[0];
          this.isNavigationBarSticky = e.isIntersecting || e.boundingClientRect.top < e.rootBounds.top;
        },
        {
          rootMargin: "-50% 0% -50% 0%",
          threshold: 0,
        },
      );
      this.contentObserver.observe(this.$refs.content);
    },

    goToIntroDay() {
      this.goToDay(0);
    },

    goToNextDay() {
      if (this.currentDay < this.numOfDays) {
        this.goToDay(this.currentDay + 1);
      }
    },

    goToPrevDay() {
      if (this.currentDay > 1) {
        this.goToDay(this.currentDay - 1);
      }
    },

    async goToFirstUndoneDay() {
      await this.toggleInList(true);
      this.goToDay(this.firstUndoneDay);
    },

    updateFirstUndoneDay() {
      let currentDay = this.days.find((d) => d.state == "current");
      if (!currentDay) {
        const firstUndoneDay = this.days.find((d) => d.state == "default");
        if (firstUndoneDay) {
          firstUndoneDay.state = "current";
        }
        currentDay = firstUndoneDay;
      }
      this.firstUndoneDay = currentDay?.day ?? 0;
    },

    goToDay(day: number | string, ignoreScrollContentIntoView = false) {
      day = Number(day);
      // if (day > 1 && (!this.isProgramAccessible || !this.isProgramStarted)) {
      //   this.scrollToStartOrBuyContainer();
      //   return;
      // }
      window.location.hash = day > 0 ? "/" + day : "/";
      this.currentDay = day;
      this.pauseAllVideos();
      if (!ignoreScrollContentIntoView) {
        this.$nextTick(() => setTimeout(() => this.scrollContentIntoView(!this.isProgramStarted), 200));
      }
      let dayLink = document.querySelector(`a[data-day='${day}']`);
      if (dayLink && (dayLink as any).scrollIntoViewIfNeeded) {
        (dayLink as any).scrollIntoViewIfNeeded();
      }
      this.showSidebar = false;
      this.$nextTick(() =>
        setTimeout(() => {
          this.contentObserver.unobserve(this.$refs.content);
          this.contentObserver.observe(this.$refs.content);
        }),
      );
      if (this.commentComponent) {
        this.commentComponent.fetchComments(true);
      }
    },

    async toggleInList(force = false) {
      try {
        this.loading = true;
        const result = await fetch(force ? this.toggleListForceUri : this.toggleListUri);
        const r = await result.text();
        this.inUserList = r == "added";
      } finally {
        this.loading = false;
      }
    },

    async resetProgress() {
      try {
        this.loading = true;
        const result = await fetch(this.resetProgressUri);
        location.href = location.protocol + "//" + location.host + location.pathname;
      } finally {
        setTimeout(() => (this.loading = false), 1000);
      }
    },

    scrollToStartOrBuyContainer() {
      this.goToDay(0, true);
      const c = document.querySelector(".program-start-or-buy-container");
      if (c) {
        setTimeout(() => c.scrollIntoView({ behavior: "smooth", block: "center" }));
      }
      if (this.isProgramAccessible && !this.isProgramStarted) {
        this.showStartProgramHint = true;
      }
    },

    scrollContentIntoView(force = false) {
      if (this.currentDay > 0 && !force) {
        setTimeout(() => document.documentElement.scrollTo({ top: 0, behavior: "smooth" }));
      } else {
        setTimeout(() => this.$refs.content.scrollIntoView({ block: "start", behavior: "smooth" }));
      }
    },

    toggleSidebar(state = null) {
      state = state === null ? !this.showSidebar : Boolean(state);
      if (state && document.documentElement.scrollTop < 120) {
        setTimeout(() => this.scrollContentIntoView(true));
      }
      this.showSidebar = state;
    },

    showResetProgressModal(open = null) {
      this.isResetProgressModalOpen = open == null ? !this.isResetProgressModalOpen : Boolean(open);
    },

    pauseAllVideos() {
      document.querySelectorAll(".jwplayer").forEach((e) => {
        const p = window["jwplayer"] && window["jwplayer"](e);
        if (p && p.pause) {
          p.pause();
        }
      });
    },

    getDay(day: number) {
      return this.days.find((d) => d.day == day);
    },

    currentDayUid() {
      const day = this.getDay(this.currentDay);
      return day?.uid || 0;
    },

    currentDayNo() {
      const day = this.getDay(this.currentDay);
      return day?.day || 0;
    },

    currentDayLabel() {
      const day = this.getDay(this.currentDay);
      return day?.label || String(this.currentDay);
    },

    firstDayLabel() {
      const day = this.getDay(1);
      return day?.label || String(1);
    },

    nextDayLabel() {
      if (this.currentDay < this.numOfDays) {
        const day = this.getDay(this.currentDay + 1);
        return day?.label || String(this.currentDay + 1);
      }
    },

    prevDayLabel() {
      if (this.currentDay > 1) {
        const day = this.getDay(this.currentDay - 1);
        return day?.label || String(this.currentDay - 1);
      }
    },

    firstUndoneDayLabel() {
      const day = this.getDay(this.firstUndoneDay);
      return day?.label;
    },

    getDayState(day: number) {
      return this.getDay(day)?.state ?? "unknown";
    },

    setDayState(day: number, state: string) {
      const d = this.getDay(day);
      if (d) {
        d.state = state;
      }
      this.updateProgress();
      this.updateFirstUndoneDay();
    },

    updateProgress() {
      if (this.numOfDays) {
        const numOfCompletedDays = this.days.filter((d) => d.state == "completed").length;
        this.progress = numOfCompletedDays / this.numOfDays;
      }
    },

    formattedProgress() {
      return Math.round(this.progress * 100) + "%";
    },
  } as any;
}
